/**
 * GTM Data Layer Manager
 *
 * Core functionality for managing dataLayer, session tracking, and event emission
 */

import { PublicDataLayerEvent, ConsentState } from './types';
import { getAttribution } from './attribution';
import { getPreviewFields } from './sgtmPreview';
import { log, warn } from './logger';

/**
 * Extract venue_slug from URL pathname.
 * Returns the first path segment, excluding static asset routes.
 *
 * Examples:
 *   /palma/explore → "palma"
 *   /palma → "palma"
 *   /api/something → null (static route)
 *   /public/asset.png → null (static route)
 *
 * @returns venue_slug or null if not a venue route
 */
export function getVenueSlugFromURL(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const pathname = window.location.pathname;
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  const firstSegment = segments[0];

  // Exclude static asset paths
  const staticPaths = ['api', 'public', 'images', 'showcase', 'assets'];
  if (staticPaths.includes(firstSegment)) {
    return null;
  }

  return firstSegment;
}

/**
 * Push venue_slug to dataLayer before GTM loads.
 * This ensures GTM tags can access venue_slug from the start,
 * even before any events fire.
 *
 * Pushes a data-only message (no `event` property) so GTM can
 * map the field into its default data model.
 */
export function pushVenueContext(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const venueSlug = getVenueSlugFromURL();

  if (!venueSlug) {
    warn('[GTM] Could not determine venue_slug for init context');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    tablz: { venue_slug: venueSlug }
  } as any);

  log('[GTM] Venue context pushed:', { tablz: { venue_slug: venueSlug } });
}

let bootstrapEmitted = false;
let fallbackSessionId: string | null = null;

/** Replayable events pushed with denied consent — re-pushed when consent is granted */
const replayBuffer: { event: PublicDataLayerEvent; ga4?: Record<string, unknown> }[] = [];

const REPLAYABLE_EVENTS = ['tablz_session_bootstrap', 'page_view'] as const;

function isAnalyticsDenied(): boolean {
  return getConsentState().analytics_storage === 'denied';
}

/**
 * Store an event for replay when consent transitions from denied to granted.
 * Only buffers tablz_session_bootstrap and page_view when analytics is denied.
 */
export function bufferReplayableEvent(event: PublicDataLayerEvent, ga4?: Record<string, unknown>): void {
  if (!REPLAYABLE_EVENTS.includes(event.event as (typeof REPLAYABLE_EVENTS)[number])) {
    return;
  }
  if (!isAnalyticsDenied()) {
    return; // Returning visitor — no replay needed
  }
  if (event.event === 'page_view') {
    const idx = replayBuffer.findIndex((entry) => entry.event.event === 'page_view');
    if (idx !== -1) {
      replayBuffer[idx] = { event, ga4 };
      return;
    }
  }
  replayBuffer.push({ event, ga4 });
}

/**
 * Push replayable events (tablz_session_bootstrap, page_view) to dataLayer or buffer for replay.
 * When analytics consent is denied: buffer only (no push) — avoids duplicate events on consent grant.
 * When analytics consent is granted: push immediately.
 */
export function pushOrBufferReplayableEvent(event: PublicDataLayerEvent, ga4?: Record<string, unknown>): void {
  if (!REPLAYABLE_EVENTS.includes(event.event as (typeof REPLAYABLE_EVENTS)[number])) {
    return;
  }
  if (isAnalyticsDenied()) {
    bufferReplayableEvent(event, ga4);
  }
  else {
    pushToDataLayer(event, ga4);
  }
}

/**
 * Re-push buffered events with the current (now granted) consent state.
 * Called after updateConsentMode when the user grants analytics consent.
 */
export function replayBufferedEvents(): void {
  if (replayBuffer.length === 0) {
    return;
  }
  const toReplay = [...replayBuffer];
  replayBuffer.length = 0;
  for (const { event, ga4 } of toReplay) {
    pushToDataLayer(event, ga4);
  }
  log('[GTM] Replayed', toReplay.length, 'event(s) with granted consent');
}

const SESSION_STORAGE_KEY = '_tablz_session_id';
const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface SessionData {
  id: string;
  lastActivity: number;
}

/**
 * Initialize the dataLayer array on window
 */
export function initializeDataLayer(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if not exists
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
}

/**
 * Get or create session ID with 30-minute sliding window
 */
export function getSessionId(): string {
  if (typeof window === 'undefined') {
    return generateEventId();
  }

  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);

    if (stored) {
      const sessionData: SessionData = JSON.parse(stored);
      const now = Date.now();

      // Check if session is still valid (within 30min)
      if (now - sessionData.lastActivity < SESSION_TTL_MS) {
        // Renew session
        sessionData.lastActivity = now;
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionData));
        return sessionData.id;
      }
    }

    // Create new session
    const newSession: SessionData = {
      id: generateEventId(),
      lastActivity: Date.now()
    };
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(newSession));
    return newSession.id;
  }
  catch (error) {
    // Fallback if localStorage is not available (private browsing, quota exceeded).
    // Reuse the same ID for the entire page lifecycle to avoid session fragmentation.
    warn('[GTM] Failed to access localStorage for session ID:', error);
    return (fallbackSessionId ??= generateEventId());
  }
}

/**
 * Generate a unique event ID (UUIDv4)
 */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get current consent state from dataLayer or default to denied
 */
export function getConsentState(): ConsentState {
  // Default state: all denied (GDPR-compliant)
  const defaultState: ConsentState = {
    ad_storage: 'denied',
    analytics_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  };

  if (typeof window === 'undefined' || !window.dataLayer) {
    return defaultState;
  }

  try {
    // Find the most recent consent update in dataLayer
    const consentEvents = window.dataLayer.filter(
      (item) => item && item[0] === 'consent' && item[1] === 'update'
    );

    if (consentEvents.length > 0) {
      const latestConsent = consentEvents[consentEvents.length - 1][2];
      if (latestConsent && typeof latestConsent === 'object') {
        return {
          ad_storage: latestConsent.ad_storage || 'denied',
          analytics_storage: latestConsent.analytics_storage || 'denied',
          ad_user_data: latestConsent.ad_user_data || 'denied',
          ad_personalization: latestConsent.ad_personalization || 'denied'
        };
      }
    }
  }
  catch (error) {
    warn('Failed to read consent state from dataLayer:', error);
  }

  return defaultState;
}

/**
 * Get environment from config or window location
 * Non-production checks are tree-shaken in production builds
 */
let cachedEnv: 'production' | 'staging' | null = null;

function getEnvironment(): 'production' | 'staging' {
  if (cachedEnv) {
    return cachedEnv;
  }

  if (typeof window === 'undefined') {
    return 'production';
  }

  // Non-production environment detection
  // Tree-shaken in production builds via Webpack DefinePlugin
  if (process.env.NODE_ENV !== 'production') {
    const hostname = window.location.hostname;

    if (hostname.includes('staging') || hostname.includes('localhost')) {
      cachedEnv = 'staging';
      return cachedEnv;
    }
  }

  cachedEnv = 'production';
  return cachedEnv;
}

/**
 * Push an event to the dataLayer.
 *
 * @param event   Tablz-schema event (event + tablz namespace)
 * @param ga4     Optional GA4 top-level fields to merge (ecommerce, page_*, etc.)
 *                These are spread at the top level so GTM GA4 tags can read them directly.
 */
export function pushToDataLayer(event: PublicDataLayerEvent, ga4?: Record<string, any>): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Ensure dataLayer exists
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  const previewFields = getPreviewFields();
  const attribution = getAttribution();

  // Enhance event with base fields — generated values take precedence over placeholders
  const enhancedEvent = {
    ...ga4,
    ...event,
    // Top-level marker so GTM triggers can match on `_tablz_source equals surface`
    // to filter out noise from other subdomains (admin, widgets, concierge, etc.)
    _tablz_source: 'surface',
    ...previewFields,
    tablz: {
      ...event.tablz,
      schema_version: '1.0',
      env: getEnvironment(),
      event_id: generateEventId(),
      timestamp_ms: Date.now(),
      session_id: getSessionId(),
      consent: getConsentState(),
      ...(attribution && { attribution })
    }
  };

  // GA4 best practice: clear previous ecommerce object before pushing a new one.
  // Always clear to prevent stale ecommerce data from leaking into subsequent events.
  // This push intentionally has NO `event` property — it is a data-only clear.
  // In Tag Assistant it appears as "Message" with undefined _event. That is normal;
  // see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clear_the_ecommerce_object
  window.dataLayer.push({ ecommerce: null } as any);

  // Push to dataLayer
  window.dataLayer.push(enhancedEvent as any);

  log('[GTM] Event pushed to dataLayer:', enhancedEvent);
}

/**
 * Emit a one-time session bootstrap event.
 * Fires exactly once per page load (module-level guard) before any other
 * forwardable events, so GA4 session attribution picks up the landing URL
 * (with UTMs intact) and the venue_id.
 */
export function emitSessionBootstrap(venueId: string): void {
  if (bootstrapEmitted) {
    return;
  }
  bootstrapEmitted = true;

  // Only pass fields that pushToDataLayer does NOT generate.
  // pushToDataLayer fills in: env, event_id, timestamp_ms, session_id, consent.
  const event: PublicDataLayerEvent = {
    event: 'tablz_session_bootstrap',
    tablz: {
      schema_version: '1.0',
      venue_slug: getVenueSlugFromURL() || 'unknown',
      venue_id: venueId
    } as PublicDataLayerEvent['tablz']
  };
  const ga4 = {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer
  };
  pushOrBufferReplayableEvent(event, ga4);
}


